var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from 'components/Button/Button';
import genUniqueId from 'utils/uniqueId';
import styles from './Modal.css';
const defaultSettings = {
    closeOnEscape: true,
    closeOnOverlayClick: false,
    scrollBarOnBody: false,
    closeButton: false,
    classNames: {}
};
const VIEWPORT_THRESHOLD = 100;
// TODO: MOVE EXISTING MODAL CODE TO USE THIS
export default class Modal extends React.Component {
    constructor(props) {
        super(props);
        // Public API
        this.show = options => {
            const id = genUniqueId();
            const { children, classNames = {} } = options, settings = __rest(options, ["children", "classNames"]);
            this.visible = true;
            this.id = id;
            this.settings = Object.assign({}, defaultSettings, settings);
            this.setState({
                children,
                classNames
            });
            document.body.classList.add('fk-modal-visible');
            document.addEventListener('keydown', this.handleKeyDown);
            if (this.settings.softRedirectTo) {
                history.pushState({}, '', this.settings.softRedirectTo);
            }
            return id;
        };
        // Public API
        this.hide = (avoidUrlChange, modalId) => this.handleHide(avoidUrlChange, modalId);
        this.resetStyles = options => {
            const { children } = this.state;
            if (!children) {
                return null;
            }
            const { classNames } = options;
            this.setState({
                classNames
            });
        };
        this.shouldClose = () => {
            if (typeof this.settings.shouldCloseModal === 'function') {
                return this.settings.shouldCloseModal();
            }
            else {
                return true;
            }
        };
        this.handleKeyDown = (e) => {
            if (this.settings.closeOnEscape && e.keyCode === 27) {
                this.handleHideModalRequest({ explicitlyClosed: true });
            }
        };
        this.handleOverlayClick = (e) => {
            if (this.settings.closeOnOverlayClick && e.target === this.root) {
                this.handleHideModalRequest({ explicitlyClosed: true });
            }
        };
        this.handleHideModalRequest = ({ avoidUrlChange, modalId, explicitlyClosed }) => {
            const canCloseModal = this.shouldClose();
            if (canCloseModal) {
                this.handleHide(avoidUrlChange, modalId, explicitlyClosed);
            }
        };
        this.handleHide = (avoidUrlChange, modalId, explicitlyClosed) => {
            if (this.visible === false || (modalId && this.id !== modalId)) {
                return;
            }
            this.visible = false;
            if (typeof this.settings.onClose === 'function') {
                this.settings.onClose(explicitlyClosed);
            }
            document.body.classList.remove('fk-modal-visible');
            document.removeEventListener('keydown', this.handleKeyDown);
            if (this.root) {
                this.root.addEventListener('transitionend', this.clearChildren);
                this.root.classList.remove(styles['visible']);
            }
            if (!avoidUrlChange && this.settings.revertUrl && this.settings.softRedirectTo) {
                this.context.router.go(-1);
            }
        };
        this.handleCloseClick = (e) => {
            e.preventDefault();
            this.handleHideModalRequest({ explicitlyClosed: true });
        };
        this.clearChildren = () => {
            this.root && this.root.removeEventListener('transitionend', this.clearChildren);
            this.setState({
                children: null
            });
        };
        this.rootRef = c => (this.root = c);
        this.dialogRef = c => (this.dialog = c);
        this.settings = Object.assign({}, defaultSettings);
        this.state = {
            children: null,
            classNames: {}
        };
    }
    shouldComponentUpdate(_, nextState) {
        return this.state.children !== nextState.children || this.state.classNames !== nextState.classNames;
    }
    componentDidUpdate() {
        if (this.visible) {
            window.requestAnimationFrame(() => {
                this.root && this.root.classList.add(styles['visible']);
                document.body.style.overflow = 'hidden';
                if (!this.dialog || !this.root)
                    return;
                const rect = this.dialog.getBoundingClientRect();
                if (window.innerHeight < VIEWPORT_THRESHOLD + rect.height) {
                    this.root.style.alignItems = 'flex-start';
                }
                else {
                    this.root.style.removeProperty('alignItems');
                }
            });
        }
        else {
            window.requestAnimationFrame(() => {
                document.body.style.removeProperty('overflow');
            });
        }
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }
    render() {
        const { children, classNames } = this.state;
        if (!children)
            return null;
        const { closeButton, scrollBarOnBody } = this.settings;
        const overlay = cx(styles['modal'], classNames.overlay, { [styles['scrollable']]: scrollBarOnBody });
        const dialog = cx(styles['modal-dialog'], classNames.dialog);
        const closeButtonClass = cx(styles['close-btn'], classNames.closeButton);
        return (<div className={overlay} ref={this.rootRef} onClick={this.handleOverlayClick} id={this.settings.overlayId}>
				<div className={dialog} ref={this.dialogRef}>
					{closeButton && (<Button data-fdp-tracking-test="modal-close-btn" onClick={this.handleCloseClick} className={closeButtonClass}>
							✕
						</Button>)}
					{children}
				</div>
			</div>);
    }
}
Modal.contextTypes = {
    router: PropTypes.object.isRequired
};
